import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ChevronRight, ShoppingCart, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';

const KineticHero = ({ productData, handleBuyNow, handleAddToCart, scrollY }) => {
  const heroRef = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Parallax effects
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  // Smooth spring physics
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });

  // Text reveal animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const lineVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      clipPath: "inset(0 100% 0 0)"
    },
    visible: {
      opacity: 1,
      y: 0,
      clipPath: "inset(0 0% 0 0)",
      transition: {
        duration: 0.8,
        ease: [0.6, 0.01, 0.05, 0.95],
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.2, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: [0.6, 0.01, 0.05, 0.95],
      },
    },
  };

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated background glow */}
      <motion.div 
        className="hero-bg-glow"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Text Content - Masked Line Reveal */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ opacity }}
          className="hero-content space-y-6"
        >
          <motion.div variants={lineVariants}>
            <Badge className="bg-gold/20 text-gold border-gold/30 text-sm px-4 py-1">
              Limited Edition
            </Badge>
          </motion.div>

          <motion.h1 
            className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight overflow-hidden"
          >
            <motion.div variants={lineVariants}>
              <span className="text-white block">MADTOYS</span>
            </motion.div>
            <motion.div variants={lineVariants}>
              <span className="hero-gradient-text block">Rotating Windmill</span>
            </motion.div>
            <motion.div variants={lineVariants}>
              <span className="hero-gradient-text block">Night Light</span>
            </motion.div>
          </motion.h1>

          <motion.p 
            variants={lineVariants}
            className="text-xl text-gray-300 leading-relaxed"
          >
            {productData.tagline}
          </motion.p>

          <motion.div variants={lineVariants} className="flex items-baseline gap-3">
            <span className="text-5xl font-bold text-gold">₹{productData.price.toLocaleString('en-IN')}</span>
            <span className="text-gray-500 line-through text-xl">₹3,049</span>
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">44% OFF</Badge>
          </motion.div>

          <motion.div 
            variants={lineVariants}
            className="flex flex-wrap gap-4 pt-4"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                onClick={handleBuyNow} 
                size="lg" 
                className="bg-gold hover:bg-gold-dark text-dark-primary font-bold text-lg px-8 py-6 relative overflow-hidden group"
              >
                <motion.span
                  className="absolute inset-0 bg-white"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{ opacity: 0.2 }}
                />
                Buy Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                onClick={() => navigate('/diy-components')}
                size="lg" 
                variant="outline" 
                className="border-gold text-gold hover:bg-gold/10 text-lg px-8 py-6"
              >
                <Package className="w-5 h-5 mr-2" />
                DIY Components
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Product Image - 3D Parallax */}
        <motion.div 
          className="hero-image-container relative"
          style={{ y: smoothY, scale }}
        >
          <div className="product-glow" />
          <motion.img 
            src={productData.images[0]} 
            alt={productData.name}
            variants={imageVariants}
            initial="hidden"
            animate="visible"
            className="w-full h-auto object-contain hero-product-image"
            style={{
              filter: 'drop-shadow(0 0 60px rgba(212, 175, 55, 0.4))',
            }}
          />

          {/* Floating elements */}
          <motion.div
            className="absolute top-10 right-10 text-gold text-6xl"
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            ✨
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="scroll-line" />
      </motion.div>
    </section>
  );
};

export default KineticHero;
